import { collection, addDoc, getDocs, deleteDoc, doc, getDoc, runTransaction, query, where, orderBy, limit, startAfter, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { computeCurviness } from '../utils/curviness';
import { reverseCountry, slugify } from '../utils/geocoding';
import { track } from './analytics';
import { bestDisplayName } from '../utils/formatters';
import { getProfile } from './profileService';
import { buildBusinessSnapshot } from './businessIdentityService';

const PAGE_SIZE = 10;

async function uploadImages(routeId, files) {
  if (!storage || !files.length) return [];
  const urls = [];
  for (const file of files) {
    const ext = file.name.split('.').pop();
    const path = `routeImages/${routeId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const storageRef = ref(storage, path);
    const snap = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snap.ref);
    urls.push(url);
  }
  return urls;
}

// ── Route similarity (duplicate detection on publish) ──────────────────────
// Fingerprint a route as the set of ~280 m grid cells its geometry passes
// through; two routes covering the same roads share most cells.
const SIM_GRID = 0.0025;

function routeCells(geometry) {
  const g = typeof geometry === 'string' ? safeParse(geometry) : geometry;
  const coords = g?.coordinates;
  const cells = new Set();
  if (!coords) return cells;
  for (const [lng, lat] of coords) cells.add(`${Math.round(lat / SIM_GRID)},${Math.round(lng / SIM_GRID)}`);
  return cells;
}
function safeParse(s) { try { return JSON.parse(s); } catch { return null; } }
function jaccard(a, b) {
  if (!a.size || !b.size) return 0;
  const [small, big] = a.size < b.size ? [a, b] : [b, a];
  let inter = 0;
  for (const k of small) if (big.has(k)) inter++;
  return inter / (a.size + b.size - inter);
}

/**
 * Find an already-published route very similar to `route`, or null. Pre-filters
 * by start proximity + similar distance (cheap), then compares grid fingerprints.
 */
export async function findSimilarRoute(route, { threshold = 0.6, excludeId } = {}) {
  if (!db || !route?.geometry) return null;
  const start = (route.waypoints || []).find(w => w.lat != null);
  if (!start) return null;
  const myCells = routeCells(route.geometry);
  if (myCells.size < 3) return null;
  const dist = route.distance ?? null;

  const D = 0.04; // ~4.4 km latitude band
  let snap;
  try {
    snap = await getDocs(query(
      collection(db, 'publishedRoutes'),
      where('startLat', '>=', start.lat - D),
      where('startLat', '<=', start.lat + D),
      limit(80),
    ));
  } catch { return null; }

  let best = null;
  for (const d of snap.docs) {
    if (excludeId && d.id === excludeId) continue;
    const r = d.data();
    if (r.startLng == null || Math.abs(r.startLng - start.lng) > D) continue;
    if (dist != null && r.distance != null && Math.abs(r.distance - dist) / Math.max(dist, 1) > 0.35) continue;
    let cells;
    if (Array.isArray(r.cellKeys) && r.cellKeys.length) cells = new Set(r.cellKeys);
    else if (r.geometry) cells = routeCells(r.geometry);
    else continue;
    const score = jaccard(myCells, cells);
    if (score >= threshold && (!best || score > best.score)) {
      best = { id: d.id, name: r.name, userName: r.userName, score: Math.round(score * 100) };
    }
  }
  return best;
}

export async function publishRoute({ user, route, name, description, imageFiles, videoUrls = [] }) {
  if (!db) throw new Error('Firestore not available');

  const routeId = `${user.uid}_${Date.now()}`;
  const imageUrls = await uploadImages(routeId, imageFiles);

  // Derive start/end names and loop flag from waypoints
  const wps = route.waypoints;
  const nonLoop = wps.filter(wp => !wp.isLoopEnd);
  const startWp = nonLoop[0];
  const endWp   = nonLoop[nonLoop.length - 1];
  const isLoop  = wps.some(wp => wp.isLoopEnd) || (
    startWp && endWp && startWp !== endWp &&
    Math.abs(startWp.lat - endWp.lat) < 0.002 &&
    Math.abs(startWp.lng - endWp.lng) < 0.002
  );

  // Discovery metadata (best-effort, never blocks publishing).
  const curv = computeCurviness(route.geometry);
  const loc  = startWp?.lat != null
    ? await reverseCountry(startWp.lat, startWp.lng).catch(() => null)
    : null;

  // Verified-business indicator, snapshotted at publish time — same tradeoff
  // as tours' creatorBadge (see tourService.js): a business verified later
  // doesn't retroactively badge older routes.
  const profile = await getProfile(user.uid).catch(() => null);
  const creatorBadge = {
    businessVerified: profile?.businessVerifiedStatus === 'verified',
  };

  const data = {
    userId:      user.uid,
    userName:    bestDisplayName(user),
    userPhoto:   user.photoURL || null,
    business:    buildBusinessSnapshot(profile),
    creatorBadge,
    name:        name.trim(),
    description: description.trim(),
    startName:   startWp?.name || '',
    endName:     isLoop ? (startWp?.name || '') : (endWp?.name || ''),
    isLoop:      isLoop || false,
    startLat:    startWp?.lat ?? null,
    startLng:    startWp?.lng ?? null,
    waypoints:   wps.map(wp => ({ lat: wp.lat, lng: wp.lng, name: wp.name || '' })),
    geometry:    route.geometry ? JSON.stringify(route.geometry) : null,
    cellKeys:    route.geometry ? Array.from(routeCells(route.geometry)).slice(0, 5000) : null,
    distance:    route.distance ?? null,
    duration:    route.duration ?? null,
    curviness:      curv ? Math.round(curv.degPerKm) : null,
    curvinessLevel: curv ? curv.level : null,
    country:        loc?.country || null,
    countryCode:    loc?.countryCode || null,
    countrySlug:    loc?.country ? slugify(loc.country) : null,
    region:         loc?.region || null,
    imageUrls,
    videoUrls,
    likeCount:   0,
    publishedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'publishedRoutes'), data);
  track('route_published', { route_id: docRef.id });
  return docRef.id;
}

export async function unpublishRoute(routeId) {
  if (!db) throw new Error('Firestore not available');
  await deleteDoc(doc(db, 'publishedRoutes', routeId));
}

export async function getUserRating(routeId, userId) {
  if (!db || !userId) return null;
  const snap = await getDoc(doc(db, 'publishedRoutes', routeId, 'ratings', userId));
  return snap.exists() ? snap.data().stars : null;
}

export async function rateRoute(routeId, userId, stars) {
  if (!db) throw new Error('Firestore not available');
  const routeRef  = doc(db, 'publishedRoutes', routeId);
  const ratingRef = doc(db, 'publishedRoutes', routeId, 'ratings', userId);

  await runTransaction(db, async tx => {
    const [routeSnap, ratingSnap] = await Promise.all([tx.get(routeRef), tx.get(ratingRef)]);
    if (!routeSnap.exists()) throw new Error('Route no longer exists');

    const { ratingTotal = 0, ratingCount = 0 } = routeSnap.data();
    const wasRated = ratingSnap.exists();
    const oldStars = wasRated ? ratingSnap.data().stars : 0;

    const newTotal = ratingTotal - oldStars + stars;
    const newCount = wasRated ? ratingCount : ratingCount + 1;

    tx.set(ratingRef, { stars, userId, ratedAt: serverTimestamp() });
    tx.update(routeRef, {
      ratingTotal:   newTotal,
      ratingCount:   newCount,
      averageRating: newTotal / newCount,
    });
  });
}

function parseRoute(d) {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    geometry: data.geometry ? JSON.parse(data.geometry) : null,
  };
}

export async function getUserStats(userId) {
  if (!db) return { totalRoutes: 0, avgRating: null, totalTours: 0 };
  const [routeSnap, tourSnap] = await Promise.all([
    getDocs(query(collection(db, 'publishedRoutes'), where('userId', '==', userId))),
    getDocs(query(collection(db, 'tours'), where('userId', '==', userId), where('status', '==', 'published'))),
  ]);
  const routes = routeSnap.docs.map(d => d.data());
  const tours  = tourSnap.docs.map(d => d.data());
  // Blend route + tour ratings into one creator average.
  const rated  = [...routes, ...tours].filter(r => r.ratingCount >= 1 && r.averageRating != null);

  // Tour-only aggregate, weighted by review count — used on a tour page so a
  // brand-new listing can still show the operator's track record.
  const ratedTours   = tours.filter(t => t.ratingCount >= 1 && t.averageRating != null);
  const tourReviews  = ratedTours.reduce((s, t) => s + t.ratingCount, 0);
  const tourRatingWt = ratedTours.reduce((s, t) => s + t.averageRating * t.ratingCount, 0);

  return {
    totalRoutes: routes.length,
    avgRating:   rated.length > 0 ? rated.reduce((s, r) => s + r.averageRating, 0) / rated.length : null,
    totalTours:  tourSnap.size,
    tourReviewCount: tourReviews,
    tourAvgRating:   tourReviews > 0 ? tourRatingWt / tourReviews : null,
    ratedTourCount:  ratedTours.length,
  };
}

export async function fetchUserRoutes(userId) {
  if (!db) return [];
  const snap = await getDocs(query(collection(db, 'publishedRoutes'), where('userId', '==', userId), limit(50)));
  return snap.docs.map(parseRoute)
    .sort((a, b) => (b.publishedAt?.seconds ?? 0) - (a.publishedAt?.seconds ?? 0));
}

// Routes by any of `uids` (Firestore `in` allows 30 per query, so chunked) —
// newest first. Powers the Community "Following" filter.
export async function fetchRoutesByUsers(uids) {
  if (!db || !uids?.length) return [];
  const chunks = [];
  for (let i = 0; i < uids.length; i += 30) chunks.push(uids.slice(i, i + 30));
  const snaps = await Promise.all(chunks.map(c =>
    getDocs(query(collection(db, 'publishedRoutes'), where('userId', 'in', c), limit(100)))));
  return snaps.flatMap(s => s.docs.map(parseRoute))
    .sort((a, b) => (b.publishedAt?.seconds ?? 0) - (a.publishedAt?.seconds ?? 0))
    .slice(0, 100);
}

// ── Discovery: server-side category queries (work across ALL data) ──
export async function fetchRoutesByCountry(slug) {
  if (!db) return [];
  const snap = await getDocs(query(
    collection(db, 'publishedRoutes'),
    where('countrySlug', '==', slug),
    orderBy('publishedAt', 'desc'),
    limit(100)
  ));
  return snap.docs.map(parseRoute);
}

export async function fetchCurvyRoutes() {
  if (!db) return [];
  const snap = await getDocs(query(
    collection(db, 'publishedRoutes'),
    where('curvinessLevel', '==', 3),
    orderBy('publishedAt', 'desc'),
    limit(100)
  ));
  return snap.docs.map(parseRoute);
}

export async function fetchPublishedRoute(routeId) {
  if (!db) return null;
  const snap = await getDoc(doc(db, 'publishedRoutes', routeId));
  if (!snap.exists()) return null;
  return parseRoute(snap);
}

export async function fetchAllPublishedRoutes() {
  if (!db) return [];
  const snap = await getDocs(query(
    collection(db, 'publishedRoutes'),
    orderBy('publishedAt', 'desc'),
    limit(200)
  ));
  return snap.docs.map(parseRoute);
}

export async function fetchPublishedRoutes(afterDoc = null) {
  if (!db) return { routes: [], lastDoc: null, hasMore: false };
  const constraints = [orderBy('publishedAt', 'desc'), limit(PAGE_SIZE)];
  if (afterDoc) constraints.splice(1, 0, startAfter(afterDoc));
  const snap = await getDocs(query(collection(db, 'publishedRoutes'), ...constraints));
  return {
    routes:  snap.docs.map(parseRoute),
    lastDoc: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === PAGE_SIZE,
  };
}
